import { useState } from 'react';
import { Send, CheckCircle2, AlertCircle, Building2, Loader2 } from 'lucide-react';
import { getBackendUrl } from '../../config/api';
import ScrollReveal from './ScrollReveal';

const CATEGORIES = [
  { value: 'demo', label: 'Book a Demo', subject: 'Demo Request — Crawler Vision' },
  { value: 'general', label: 'General Enquiry', subject: 'General Enquiry' },
  { value: 'payment', label: 'Payment Support', subject: 'Payment Support Request' },
  { value: 'premium', label: 'Premium / Enterprise', subject: 'Premium Account Enquiry' },
  { value: 'partnership', label: 'Partnership', subject: 'Partnership Opportunity' },
];

const companies = [
  {
    name: 'DNS Stack Pvt Ltd',
    address: '2nd Floor, Kalkat Bhawan, Punjab Innovation Mission, Gmada Aerocity, SAS Nagar, Mohali, Punjab 140306, India',
    url: 'https://dnsstack.com',
  },
  {
    name: 'Techtonic Systems Media and Research LLC',
    address: '701 Tillery Street Unit 12, 2368, Austin, TX 78702, USA',
    url: 'https://techtonic.systems',
  },
];

const ContactSection = () => {
  const [form, setForm] = useState({ name: '', email: '', category: 'demo', subject: CATEGORIES[0].subject, message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { type: 'success'|'error', message }

  const handleCategoryChange = (e) => {
    const cat = CATEGORIES.find((c) => c.value === e.target.value);
    setForm((prev) => ({ ...prev, category: e.target.value, subject: cat?.subject || prev.subject }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);

    try {
      const res = await fetch(`${getBackendUrl()}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult({ type: 'success', message: data.message || 'Message sent successfully!' });
        setForm({ name: '', email: '', category: 'demo', subject: CATEGORIES[0].subject, message: '' });
      } else {
        setResult({ type: 'error', message: data.detail || data.message || 'Something went wrong. Please try again.' });
      }
    } catch {
      setResult({ type: 'error', message: 'Network error. Please try again or email support@crawlervision.com directly.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <ScrollReveal>
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1.5 bg-cv-sky/10 text-cv-navy text-xs font-bold uppercase tracking-wider rounded-full border border-cv-sky/30 mb-4">
              Contact
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-cv-navy mb-4">Get in Touch</h2>
            <p className="text-cv-navy/70 max-w-xl mx-auto text-lg">
              Book a demo, ask a question, or explore partnerships. We typically respond within 24-48 business hours.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid lg:grid-cols-5 gap-10">
          {/* Form — takes 3 columns */}
          <ScrollReveal className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              className="bg-cv-navy/5 rounded-2xl border border-cv-navy/15 p-6 sm:p-8 shadow-sm"
            >
              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="cf-name" className="block text-sm font-semibold text-cv-navy/80 mb-1.5">
                    Your Name <span className="text-cv-orange/80">*</span>
                  </label>
                  <input
                    id="cf-name"
                    type="text"
                    required
                    minLength={2}
                    maxLength={100}
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="John Doe"
                    className="w-full px-4 py-3 border-2 border-cv-navy/15 rounded-xl text-cv-navy/90 placeholder:text-cv-navy/40 focus:outline-none focus:ring-2 focus:ring-cv-navy focus:border-transparent transition-all text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="cf-email" className="block text-sm font-semibold text-cv-navy/80 mb-1.5">
                    Email Address <span className="text-cv-orange/80">*</span>
                  </label>
                  <input
                    id="cf-email"
                    type="email"
                    required
                    maxLength={200}
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                    placeholder="you@company.com"
                    className="w-full px-4 py-3 border-2 border-cv-navy/15 rounded-xl text-cv-navy/90 placeholder:text-cv-navy/40 focus:outline-none focus:ring-2 focus:ring-cv-navy focus:border-transparent transition-all text-sm"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="cf-category" className="block text-sm font-semibold text-cv-navy/80 mb-1.5">
                    Category <span className="text-cv-orange/80">*</span>
                  </label>
                  <select
                    id="cf-category"
                    value={form.category}
                    onChange={handleCategoryChange}
                    className="w-full px-4 py-3 border-2 border-cv-navy/15 rounded-xl text-cv-navy/90 focus:outline-none focus:ring-2 focus:ring-cv-navy focus:border-transparent transition-all text-sm bg-white"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="cf-subject" className="block text-sm font-semibold text-cv-navy/80 mb-1.5">
                    Subject <span className="text-cv-orange/80">*</span>
                  </label>
                  <input
                    id="cf-subject"
                    type="text"
                    required
                    minLength={2}
                    maxLength={200}
                    value={form.subject}
                    onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-cv-navy/15 rounded-xl text-cv-navy/90 placeholder:text-cv-navy/40 focus:outline-none focus:ring-2 focus:ring-cv-navy focus:border-transparent transition-all text-sm"
                  />
                </div>
              </div>

              <div className="mb-5">
                <label htmlFor="cf-message" className="block text-sm font-semibold text-cv-navy/80 mb-1.5">
                  Message <span className="text-cv-orange/80">*</span>
                </label>
                <textarea
                  id="cf-message"
                  required
                  minLength={10}
                  maxLength={5000}
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                  placeholder={form.category === 'demo' ? "Tell us about your team and what you'd like to see in the demo..." : 'How can we help you?'}
                  className="w-full px-4 py-3 border-2 border-cv-navy/15 rounded-xl text-cv-navy/90 placeholder:text-cv-navy/40 focus:outline-none focus:ring-2 focus:ring-cv-navy focus:border-transparent transition-all text-sm resize-none"
                />
              </div>

              {/* Result message */}
              {result && (
                <div className={`mb-5 p-4 rounded-xl flex items-start gap-3 text-sm ${
                  result.type === 'success'
                    ? 'bg-cv-teal/10 border border-cv-teal/40 text-cv-navy'
                    : 'bg-cv-orange/10 border border-cv-orange/40 text-cv-orange'
                }`}>
                  {result.type === 'success' ? <CheckCircle2 className="w-5 h-5 text-cv-navy flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 text-cv-orange flex-shrink-0 mt-0.5" />}
                  <span>{result.message}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 px-6 bg-cv-navy text-white font-bold rounded-xl hover:shadow-lg shadow-sm transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Send className="w-4 h-4" /> Send Message</>}
              </button>
            </form>
          </ScrollReveal>

          {/* Sidebar — takes 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            <ScrollReveal delay={0.08}>
              <div className="text-xs font-semibold text-cv-navy uppercase tracking-wider">
                A Product Of DNS Stack Pvt Ltd and Techtonic Systems Media and Research LLC
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <div className="bg-cv-sky/10 rounded-2xl border border-cv-sky/30 p-6">
                <h3 className="text-sm font-bold text-cv-navy mb-3">Direct Email Channels</h3>
                <ul className="space-y-2.5 text-sm">
                  {[
                    { label: 'General Support', email: 'support@crawlervision.com' },
                    { label: 'Payment Issues', email: 'payments@crawlervision.com' },
                    { label: 'Premium & Demo', email: 'premium@crawlervision.com' },
                    { label: 'Partnerships', email: 'partners@crawlervision.com' },
                  ].map((ch) => (
                    <li key={ch.email} className="flex items-center justify-between">
                      <span className="text-cv-navy/70">{ch.label}</span>
                      <a href={`mailto:${ch.email}`} className="text-cv-navy hover:underline font-medium text-xs">{ch.email}</a>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>

            {companies.map((c, i) => (
              <ScrollReveal key={c.name} delay={0.15 + i * 0.05}>
                <div className="bg-cv-navy/5 rounded-2xl border border-cv-navy/10 p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-cv-navy" />
                    <span className="text-xs font-bold uppercase tracking-wider text-cv-navy">Company</span>
                  </div>
                  <h4 className="font-bold text-cv-navy text-sm mb-1">{c.name}</h4>
                  <p className="text-xs text-cv-navy/70 leading-relaxed mb-1">{c.address}</p>
                  <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-cv-navy hover:underline">{c.url}</a>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
